import { z } from 'zod'
import type { TFunction } from 'i18next'

/**
 * Creates the assistant form validation schema with translated error messages.
 *
 * @param t - The translation function from react-i18next
 * @returns Zod schema with translated error messages
 */
export function createAssistantSchema(t: TFunction) {
  return z.object({
    // ID (for reference in components)
    id: z.number().optional(),

    // About Tab
    name: z.string().min(1, t('app.validation.name_required')),
    customer_id: z.number().nullish(),
    timezone: z.string().nullish(),
  prompt_scenarios: z
    .object({
      about_business: z.string().nullish(),
      role: z.string().nullish(),
      faq: z.string().nullish(),
      transfer_policy: z.string().nullish(),
      call_policy: z.string().nullish(),
      context: z.string().nullish(),
    })
    .nullish()
    .transform((val) => val ?? {}),
  config: z.record(z.string(), z.any()).nullish().transform((val) => val ?? {}),

  // Personality Tab
    voice_id: z.number().nullish(),
    default_language: z.string().nullish(),
    additional_languages: z.array(z.string()).nullish(),
    lexicon: z
      .record(
        z.string(),
        z.array(
          z.object({
            original: z.string(),
            replacement: z.string(),
            type: z.enum(['replacement', 'phonetic']),
          })
        )
      )
      .nullish(),
    greeting: z.string().nullish(),

    // Tasks Tab
    prompt: z.string().nullish(),
    post_call_prompt: z.string().nullish(),

    // Telephony Tab
    phone_number_id: z.number().nullish(),
    fallback_number: z.string().nullish(),
    notification_user: z.number().nullish(),
    notification_outcomes: z.array(z.string()).nullish(),
    ai_model: z.string().nullish(),
    ai_deployment_id: z.number().nullish(),
    from_id: z.string().nullish(),
    whatsapp_image: z.string().nullish(),
    is_active: z.boolean().nullish(),
  })
}

/**
 * Type for the assistant form data, inferred from the schema.
 * Uses a static version of the schema for type inference.
 */
const staticSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1),
  customer_id: z.number().nullish(),
  timezone: z.string().nullish(),
  prompt_scenarios: z
    .object({
      about_business: z.string().nullish(),
      role: z.string().nullish(),
      faq: z.string().nullish(),
      transfer_policy: z.string().nullish(),
      call_policy: z.string().nullish(),
      context: z.string().nullish(),
    })
    .nullish()
    .transform((val) => val ?? {}),
  config: z.record(z.string(), z.any()).nullish().transform((val) => val ?? {}),
  voice_id: z.number().nullish(),
  default_language: z.string().nullish(),
  additional_languages: z.array(z.string()).nullish(),
  lexicon: z
    .record(
      z.string(),
      z.array(
        z.object({
          original: z.string(),
          replacement: z.string(),
          type: z.enum(['replacement', 'phonetic']),
        })
      )
    )
    .nullish(),
  greeting: z.string().nullish(),
  prompt: z.string().nullish(),
  post_call_prompt: z.string().nullish(),
  phone_number_id: z.number().nullish(),
  fallback_number: z.string().nullish(),
  notification_user: z.number().nullish(),
  notification_outcomes: z.array(z.string()).nullish(),
  ai_model: z.string().nullish(),
  ai_deployment_id: z.number().nullish(),
  from_id: z.string().nullish(),
  whatsapp_image: z.string().nullish(),
  is_active: z.boolean().nullish(),
})

export type AssistantFormData = z.infer<typeof staticSchema>
